import { z } from "zod";

/**
 * PEP-style Performance Tasks.
 *
 * A task is a scenario with sources (articles, letters, posters, tables, graphs) and
 * two to four parts. Each part holds a handful of items in the item types the Student
 * Assessment Unit uses: single/multiple select, table grid, matching, sequencing,
 * fill-in-the-blank with a word bank, numeric answers with working, grid drawing, short
 * constructed responses and extended writing (essay, letter, story, report, speech).
 *
 * Objective items are marked by `autoGradeItem` below (shared by server and client).
 * Constructed responses are marked by the AI marker (server/services/performanceTasks/grader.ts)
 * against the item's rubric, or by an admin in the marking queue when AI is unavailable.
 *
 * Text fields accept the TaskText subset: paragraphs separated by blank lines, "# " headings,
 * "- " bullets, "1. " numbered lines, "| a | b |" table rows (first row = header),
 * "> " callouts, **bold**, *italic* and inline $KaTeX$ (write money as \$).
 */

export const PT_SUBJECTS = [
  // PEP, grades 4 to 6.
  "Mathematics", "Language Arts", "Science", "Social Studies",
  // CSEC, grade 11. Paper 2 is structured and extended response, which is exactly what this
  // engine already does: multi-part items, rubric criteria and AI marking against a mark scheme.
  "English A", "English B", "Additional Mathematics", "Biology", "Chemistry", "Physics",
  "Principles of Accounts", "Principles of Business", "Economics", "Geography", "History",
  "Information Technology", "Office Administration", "Electronic Document Preparation and Management",
  // CAPE, grades 12 and 13.
  "Pure Mathematics", "Applied Mathematics", "Accounting", "Management of Business",
  "Communication Studies", "Caribbean Studies", "Sociology", "Law", "Digital Media",
] as const;
export type PtSubject = (typeof PT_SUBJECTS)[number];
export const PT_GRADES = [4, 5, 6, 7, 8, 9, 10, 11, 12, 13] as const;

/** Which examination a task belongs to, and which paper within it. */
export const PT_EXAMS = ["PEP", "CSEC", "CAPE"] as const;
export const PT_PAPERS = ["paper1", "paper2", "paper3"] as const;

export const ACHIEVEMENT_LEVELS = ["Beginning", "Developing", "Proficient", "Highly Proficient"] as const;
export type AchievementLevel = (typeof ACHIEVEMENT_LEVELS)[number];

/** EduFocal practice bands (percent of available marks). MoEY publishes scaled cut scores, not raw ones. */
export function achievementLevel(percent: number): AchievementLevel {
  if (percent >= 85) return "Highly Proficient";
  if (percent >= 65) return "Proficient";
  if (percent >= 40) return "Developing";
  return "Beginning";
}

export const DEFAULT_XP_REWARD = 300;
/** XP for a graded attempt: 35% for finishing, the rest scales with the score. */
export function xpForAttempt(xpReward: number, percent: number): number {
  const p = Math.max(0, Math.min(100, percent)) / 100;
  return Math.round(xpReward * (0.35 + 0.65 * p));
}

const text = z.string().max(6000);
const short = z.string().max(400);
const id = z.string().min(1).max(64);

export const sourceKinds = ["article", "letter", "poster", "poem", "passage", "interview", "comments", "table", "graph", "diagram", "notice", "advertisement", "email", "data"] as const;

export const sourceSchema = z.object({
  id,
  title: short,
  kind: z.enum(sourceKinds),
  body: text, // TaskText
  attribution: short.optional(),
  image: z.object({ url: z.string().max(600), alt: short }).optional(),
  // Bar chart rendered natively so tasks do not depend on images.
  chart: z.object({
    title: short.optional(),
    yLabel: short.optional(),
    series: z.array(z.object({ label: short, value: z.number(), color: z.string().max(40).optional() })).min(1).max(12),
  }).optional(),
});
export type TaskSource = z.infer<typeof sourceSchema>;

export const criterionSchema = z.object({
  id,
  name: short,
  points: z.number().min(0.5).max(20),
  description: text, // what full marks look like
});
export type Criterion = z.infer<typeof criterionSchema>;

const option = z.object({ id, text: short });

const itemBase = {
  id,
  label: z.string().max(12).optional(), // "1A", "3", "4b"
  prompt: text,
  strand: short.optional(),
  objective: short.optional(),
  points: z.number().min(0.5).max(40),
  keyDescription: text.optional(), // marker's note; never sent to students before submission
  dependsOn: z.array(id).max(6).optional(), // earlier items whose responses the marker must see
  hint: short.optional(),
};

export const singleSelectItem = z.object({ ...itemBase, type: z.literal("single_select"), options: z.array(option).min(2).max(8), key: z.object({ correct: id }) });
export const multiSelectItem = z.object({ ...itemBase, type: z.literal("multi_select"), options: z.array(option).min(3).max(10), selectCount: z.number().int().min(2).max(6), key: z.object({ correct: z.array(id).min(2).max(6) }), partialCredit: z.boolean().optional() });
export const tableGridItem = z.object({ ...itemBase, type: z.literal("table_grid"), rows: z.array(option).min(2).max(10), columns: z.array(option).min(2).max(5), key: z.object({ answers: z.record(id, id) }) });
export const matchingItem = z.object({ ...itemBase, type: z.literal("matching"), left: z.array(option).min(2).max(8), right: z.array(option).min(2).max(10), key: z.object({ pairs: z.record(id, id) }) });
export const sequenceItem = z.object({ ...itemBase, type: z.literal("sequence"), items: z.array(option).min(3).max(8), key: z.object({ order: z.array(id).min(3).max(8) }) });
export const fillBlankItem = z.object({
  ...itemBase,
  type: z.literal("fill_blank"),
  text, // blanks written as [[b1]]
  blanks: z.array(z.object({ id, options: z.array(short).max(10).optional() })).min(1).max(8),
  wordBank: z.array(short).max(16).optional(),
  key: z.object({ answers: z.record(id, z.array(short).min(1)) }),
});
export const numericItem = z.object({
  ...itemBase,
  type: z.literal("numeric"),
  unit: z.string().max(24).optional(),
  // Either a fixed answer or an expression over earlier numeric responses, e.g. "ceil({q2} / 10)".
  key: z.object({ answer: z.number().optional(), expr: z.string().max(200).optional(), tolerance: z.number().min(0).optional(), acceptRange: z.tuple([z.number(), z.number()]).optional() }),
  working: z.object({ required: z.boolean().default(true), points: z.number().min(0.5).max(20), guidance: text }).optional(),
});
export const shortResponseItem = z.object({ ...itemBase, type: z.literal("short_response"), lines: z.number().int().min(1).max(12).optional(), key: z.object({ guidance: text, rubric: z.array(criterionSchema).min(1).max(6) }) });
export const extendedResponseItem = z.object({
  ...itemBase,
  type: z.literal("extended_response"),
  form: z.enum(["essay", "letter", "story", "report", "speech", "article", "summary", "analysis", "reflection"]),
  minParagraphs: z.number().int().min(1).max(6).optional(),
  maxParagraphs: z.number().int().min(1).max(8).optional(), // the paper's stated range, e.g. "three to four paragraphs"
  checklist: z.array(short).max(8).optional(), // "When you write your essay remember to..."
  key: z.object({ guidance: text, rubric: z.array(criterionSchema).min(1).max(6) }),
});
export const choiceExplainItem = z.object({
  ...itemBase,
  type: z.literal("choice_explain"),
  options: z.array(option).min(2).max(6),
  choicePoints: z.number().min(0).max(10).optional(), // only when key.correct is set
  key: z.object({ correct: id.nullable().optional(), guidance: text, rubric: z.array(criterionSchema).min(1).max(6) }),
});
export const gridDrawItem = z.object({
  ...itemBase,
  type: z.literal("grid_draw"),
  cols: z.number().int().min(4).max(30),
  rows: z.number().int().min(4).max(30),
  unitLabel: short, // "Each square is 1 square metre"
  shapes: z.array(z.object({ id, label: short, color: z.string().max(40).optional() })).min(1).max(4),
  key: z.object({ shapes: z.record(id, z.object({ area: z.number().optional(), minArea: z.number().optional(), maxArea: z.number().optional(), rectangle: z.boolean().optional(), width: z.number().optional(), height: z.number().optional() })) }),
});

export const itemSchema = z.discriminatedUnion("type", [
  singleSelectItem, multiSelectItem, tableGridItem, matchingItem, sequenceItem, fillBlankItem,
  numericItem, shortResponseItem, extendedResponseItem, choiceExplainItem, gridDrawItem,
]);
export type TaskItem = z.infer<typeof itemSchema>;
export type ItemType = TaskItem["type"];

export const partSchema = z.object({
  id,
  title: short,
  intro: text.optional(), // TaskText shown above the items
  items: z.array(itemSchema).min(1).max(8),
});
export type TaskPart = z.infer<typeof partSchema>;

export const taskDocumentSchema = z.object({
  title: z.string().min(1).max(160),
  slug: z.string().min(1).max(120).regex(/^[a-z0-9-]+$/),
  grade: z.number().int().min(4).max(13),
  subject: z.enum(PT_SUBJECTS),
  /** PEP tasks predate these and leave them unset; CSEC and CAPE tasks must set them. */
  exam: z.enum(PT_EXAMS).optional(),
  paper: z.enum(PT_PAPERS).optional(),
  /** CAPE only: which of the Unit's three modules this covers. */
  module: z.number().int().min(1).max(3).optional(),
  theme: short.optional(),
  description: z.string().min(1).max(500),
  intro: text, // scenario
  youWillNeedTo: z.array(short).max(6).optional(),
  sources: z.array(sourceSchema).max(6),
  parts: z.array(partSchema).min(1).max(6),
  timeMinutes: z.number().int().min(20).max(240).default(90),
  xpReward: z.number().int().min(50).max(1000).default(DEFAULT_XP_REWARD),
  notesEnabled: z.boolean().default(true),
  tags: z.array(z.string().max(40)).max(8).default([]),
});
export type TaskDocument = z.infer<typeof taskDocumentSchema>;

export function slugifyTask(input: string): string {
  return input.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 120);
}

export function allItems(doc: Pick<TaskDocument, "parts">): TaskItem[] {
  return doc.parts.flatMap(p => p.items);
}

export function totalPoints(doc: Pick<TaskDocument, "parts">): number {
  return round1(allItems(doc).reduce((s, i) => s + i.points, 0));
}

/** How an item is marked: fully automatic, by the AI/admin marker, or a mix (numeric with working, choice with explanation). */
export function gradingMode(item: TaskItem): "auto" | "marker" | "mixed" {
  switch (item.type) {
    case "single_select": case "multi_select": case "table_grid": case "matching": case "sequence": case "fill_blank": case "grid_draw":
      return "auto";
    case "numeric":
      return item.working ? "mixed" : "auto";
    case "choice_explain":
      return item.key.correct ? "mixed" : "marker";
    case "short_response": case "extended_response":
      return "marker";
  }
}

/** Marks the marker (AI or admin) is responsible for on this item. */
export function markerPoints(item: TaskItem): number {
  switch (gradingMode(item)) {
    case "auto": return 0;
    case "marker": return item.points;
    case "mixed":
      if (item.type === "numeric") return item.working!.points;
      if (item.type === "choice_explain") return round1(item.points - (item.choicePoints ?? 0));
      return 0;
  }
}

export function round1(n: number): number { return Math.round(n * 10) / 10; }

// ---------- Responses ----------

export type ItemResponse =
  | string | string[] | Record<string, string> | Record<string, number[]>
  | { answer?: string; working?: string }
  | { choice?: string; explanation?: string };

export function isAnswered(item: TaskItem, r: unknown): boolean {
  if (r === undefined || r === null) return false;
  switch (item.type) {
    case "single_select": return typeof r === "string" && r.length > 0;
    case "multi_select": return Array.isArray(r) && r.length > 0;
    case "table_grid": return !!r && typeof r === "object" && Object.keys(r as object).length >= item.rows.length;
    case "matching": return !!r && typeof r === "object" && Object.keys(r as object).length >= item.left.length;
    case "sequence": return Array.isArray(r) && r.length === item.items.length;
    case "fill_blank": return !!r && typeof r === "object" && item.blanks.every(b => String((r as any)[b.id] ?? "").trim().length > 0);
    case "numeric": return !!r && typeof r === "object" && String((r as any).answer ?? "").trim().length > 0;
    case "short_response": case "extended_response": return typeof r === "string" && r.trim().length > 0;
    case "choice_explain": return !!r && typeof r === "object" && (!!(r as any).choice || String((r as any).explanation ?? "").trim().length > 0);
    case "grid_draw": return !!r && typeof r === "object" && item.shapes.every(s => Array.isArray((r as any)[s.id]) && (r as any)[s.id].length > 0);
  }
}

// ---------- Item scores ----------

export interface CriterionScore { id: string; name: string; score: number; max: number; note?: string }
export interface ItemScore {
  itemId: string;
  score: number;
  max: number;
  /** graded = final; pending = waiting on the marker. */
  status: "graded" | "pending";
  /** Portion already decided automatically (objective part of a mixed item). */
  autoScore: number;
  autoMax: number;
  correct?: boolean | null; // objective verdict, when there is one
  detail?: Record<string, boolean> | null; // per row / pair / blank / shape verdicts
  criteria?: CriterionScore[];
  feedback?: string;
  markedBy?: "auto" | "ai" | "admin";
}

function normText(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9.\-/ ]+/g, " ").replace(/\s+/g, " ").trim();
}

export function parseNumber(raw: unknown): number | null {
  if (typeof raw === "number") return Number.isFinite(raw) ? raw : null;
  if (typeof raw !== "string") return null;
  let s = raw.trim().toLowerCase().replace(/[$,]/g, "").replace(/j\$|jmd|us\$/g, "");
  s = s.replace(/[a-z²³%°]+\.?$/g, "").trim(); // trailing unit
  const frac = s.match(/^(-?\d+)\s*\/\s*(\d+)$/);
  if (frac) { const d = Number(frac[2]); return d ? Number(frac[1]) / d : null; }
  const mixed = s.match(/^(-?\d+)\s+(\d+)\s*\/\s*(\d+)$/);
  if (mixed) { const d = Number(mixed[3]); return d ? Number(mixed[1]) + Math.sign(Number(mixed[1]) || 1) * (Number(mixed[2]) / d) : null; }
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** Evaluate a key expression such as "ceil({q2} / 10)" using earlier numeric responses. */
export function evalKeyExpr(expr: string, responses: Record<string, unknown>): number | null {
  const substituted = expr.replace(/\{([a-zA-Z0-9_-]+)\}/g, (_, ref) => {
    const r = responses[ref];
    const n = parseNumber(typeof r === "object" && r ? (r as any).answer ?? (r as any).choice : r);
    return n === null ? "NaN" : `(${n})`;
  });
  if (!/^[\d\s+\-*/().,NaN]*(ceil|floor|round|min|max|abs|NaN|[\d\s+\-*/().,])*$/.test(substituted)) return null;
  if (/[a-zA-Z]/.test(substituted.replace(/ceil|floor|round|min|max|abs|NaN/g, ""))) return null;
  try {
    const fn = new Function("ceil", "floor", "round", "min", "max", "abs", `return (${substituted});`);
    const v = fn(Math.ceil, Math.floor, Math.round, Math.min, Math.max, Math.abs);
    return typeof v === "number" && Number.isFinite(v) ? v : null;
  } catch { return null; }
}

function cellsToRect(cells: number[], cols: number): { w: number; h: number; filled: boolean } {
  const xs = cells.map(c => c % cols), ys = cells.map(c => Math.floor(c / cols));
  const w = Math.max(...xs) - Math.min(...xs) + 1;
  const h = Math.max(...ys) - Math.min(...ys) + 1;
  return { w, h, filled: new Set(cells).size === w * h };
}

/**
 * Score the objective portion of an item. For marker-only items the result is pending with
 * zero auto marks. `responses` holds every response in the attempt so dependent keys resolve.
 */
export function autoGradeItem(item: TaskItem, response: unknown, responses: Record<string, unknown>): ItemScore {
  const base = (partial: Partial<ItemScore>): ItemScore => ({
    itemId: item.id, score: 0, max: item.points, status: "graded", autoScore: 0, autoMax: item.points, correct: null, detail: null, markedBy: "auto", ...partial,
  });
  const r: any = response;
  switch (item.type) {
    case "single_select": {
      const ok = typeof r === "string" && r === item.key.correct;
      return base({ score: ok ? item.points : 0, autoScore: ok ? item.points : 0, correct: ok });
    }
    case "multi_select": {
      const chosen = Array.isArray(r) ? (r as string[]) : [];
      const correct = new Set(item.key.correct);
      const hits = chosen.filter(c => correct.has(c)).length;
      const wrong = chosen.length - hits;
      const exact = hits === correct.size && wrong === 0;
      let score = exact ? item.points : 0;
      if (!exact && item.partialCredit) score = round1(Math.max(0, (hits - wrong) / correct.size) * item.points);
      return base({ score, autoScore: score, correct: exact });
    }
    case "table_grid": {
      const detail: Record<string, boolean> = {};
      let hits = 0;
      for (const row of item.rows) { const ok = !!r && r[row.id] === item.key.answers[row.id]; detail[row.id] = ok; if (ok) hits++; }
      const score = round1((hits / item.rows.length) * item.points);
      return base({ score, autoScore: score, correct: hits === item.rows.length, detail });
    }
    case "matching": {
      const detail: Record<string, boolean> = {};
      let hits = 0;
      for (const l of item.left) { const ok = !!r && r[l.id] === item.key.pairs[l.id]; detail[l.id] = ok; if (ok) hits++; }
      const score = round1((hits / item.left.length) * item.points);
      return base({ score, autoScore: score, correct: hits === item.left.length, detail });
    }
    case "sequence": {
      const order = Array.isArray(r) ? (r as string[]) : [];
      const detail: Record<string, boolean> = {};
      let hits = 0;
      item.key.order.forEach((idv, i) => { const ok = order[i] === idv; detail[idv] = ok; if (ok) hits++; });
      const all = hits === item.key.order.length;
      const score = all ? item.points : round1((hits / item.key.order.length) * item.points * 0.5);
      return base({ score, autoScore: score, correct: all, detail });
    }
    case "fill_blank": {
      const detail: Record<string, boolean> = {};
      let hits = 0;
      for (const b of item.blanks) {
        const given = normText(String(r?.[b.id] ?? ""));
        const accepted = (item.key.answers[b.id] || []).map(normText);
        const gn = parseNumber(given), ok = accepted.includes(given) || (gn !== null && accepted.some(a => parseNumber(a) === gn));
        detail[b.id] = ok; if (ok) hits++;
      }
      const score = round1((hits / item.blanks.length) * item.points);
      return base({ score, autoScore: score, correct: hits === item.blanks.length, detail });
    }
    case "numeric": {
      const answerMax = round1(item.points - (item.working?.points ?? 0));
      const given = parseNumber(r?.answer);
      let expected: number | null = item.key.answer ?? null;
      if (item.key.expr) expected = evalKeyExpr(item.key.expr, responses);
      let ok = false;
      if (given !== null) {
        if (item.key.acceptRange) ok = given >= item.key.acceptRange[0] && given <= item.key.acceptRange[1];
        else if (expected !== null) ok = Math.abs(given - expected) <= (item.key.tolerance ?? Math.max(0.01, Math.abs(expected) * 0.001));
      }
      const autoScore = ok ? answerMax : 0;
      const pending = !!item.working;
      return base({ score: autoScore, autoScore, autoMax: answerMax, correct: ok, status: pending ? "pending" : "graded", markedBy: pending ? undefined : "auto" });
    }
    case "choice_explain": {
      const cp = item.key.correct ? (item.choicePoints ?? 0) : 0;
      const ok = item.key.correct ? r?.choice === item.key.correct : null;
      const autoScore = ok ? cp : 0;
      return base({ score: autoScore, autoScore, autoMax: cp, correct: ok, status: "pending", markedBy: undefined });
    }
    case "grid_draw": {
      const detail: Record<string, boolean> = {};
      let hits = 0;
      for (const s of item.shapes) {
        const cells: number[] = Array.isArray(r?.[s.id]) ? r[s.id] : [];
        const k = item.key.shapes[s.id];
        let ok = cells.length > 0 && !!k;
        if (ok && k) {
          const area = new Set(cells).size;
          if (k.area !== undefined && area !== k.area) ok = false;
          if (k.minArea !== undefined && area < k.minArea) ok = false;
          if (k.maxArea !== undefined && area > k.maxArea) ok = false;
          const rect = cellsToRect(cells, item.cols);
          if (k.rectangle && !rect.filled) ok = false;
          if (k.width !== undefined && k.height !== undefined && !((rect.w === k.width && rect.h === k.height) || (rect.w === k.height && rect.h === k.width))) ok = false;
        }
        detail[s.id] = ok; if (ok) hits++;
      }
      const score = round1((hits / item.shapes.length) * item.points);
      return base({ score, autoScore: score, correct: hits === item.shapes.length, detail });
    }
    case "short_response": case "extended_response":
      return base({ status: "pending", autoMax: 0, markedBy: undefined });
  }
}

export function summariseScores(doc: Pick<TaskDocument, "parts">, scores: Record<string, ItemScore>) {
  const items = allItems(doc);
  let score = 0, max = 0, pending = 0;
  for (const it of items) {
    const s = scores[it.id];
    max += it.points;
    if (!s) { pending++; continue; }
    score += s.score;
    if (s.status === "pending") pending++;
  }
  const percent = max > 0 ? Math.round((score / max) * 1000) / 10 : 0;
  return { score: round1(score), max: round1(max), percent, pending, level: achievementLevel(percent) };
}

/** Strip marking keys before a task is shown to a student who has not submitted. */
export function publicTaskDocument(doc: TaskDocument): TaskDocument {
  return {
    ...doc,
    parts: doc.parts.map(p => ({
      ...p,
      items: p.items.map(it => {
        const { keyDescription: _k, key: _key, ...rest } = it as any;
        if (it.type === "numeric" && it.working) return { ...rest, working: { required: it.working.required, points: it.working.points } };
        if ((it.type === "short_response" || it.type === "extended_response" || it.type === "choice_explain")) {
          return { ...rest, key: { rubric: it.key.rubric.map(c => ({ id: c.id, name: c.name, points: c.points, description: "" })) } };
        }
        return rest;
      }),
    })),
  } as TaskDocument;
}

export const ITEM_TYPE_LABEL: Record<ItemType, string> = {
  single_select: "Multiple choice",
  multi_select: "Multiple select",
  table_grid: "Table grid",
  matching: "Matching",
  sequence: "Order",
  fill_blank: "Fill in the blanks",
  numeric: "Calculation",
  short_response: "Short answer",
  extended_response: "Extended writing",
  choice_explain: "Choose and explain",
  grid_draw: "Grid drawing",
};
